/**
 * Format code using Prettier on the server
 * @param code - The code to format
 * @param language - The language of the code
 * @returns Promise<string> - The formatted code
 */
export async function formatCode(
  code: string,
  language: string
): Promise<string> {
  try {
    console.log('Formatting with Prettier:', { code, language });
    
    const response = await fetch('/api/files/format', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, language }),
    });

    if (!response.ok) {
      throw new Error(`Formatting failed: ${response.statusText}`);
    }

    const { formattedCode } = await response.json();
    console.log('Prettier result:', formattedCode);
    return formattedCode;
  } catch (error) {
    console.warn('Failed to format code with Prettier:', error);
    // Fallback to our basic formatter
    return enhancedFormat(code, language);
  }
}

/**
 * Check if a language supports formatting
 * @param language - The language to check
 * @returns boolean - Whether the language supports formatting
 */
export function supportsFormatting(language: string): boolean {
  const supportedLanguages = [
    'javascript',
    'typescript',
    'html',
    'css',
    'json',
    'markdown',
    'python',
    'java',
    'cpp',
    'c',
    'csharp',
    'php',
    'ruby',
    'go',
    'rust',
    'sql',
    'xml',
    'yaml',
  ];

  return supportedLanguages.includes(language.toLowerCase());
}

/**
 * Enhanced formatting function as fallback when Prettier fails
 * @param code - The code to format
 * @param language - The language of the code
 * @returns string - The formatted code
 */
function enhancedFormat(code: string, language: string): string {
  if (!code.trim()) return code;

  switch (language.toLowerCase()) {
    case 'javascript':
    case 'js':
    case 'typescript':
    case 'ts':
      return basicJavaScriptFormat(code);

    case 'json':
      try {
        const parsed = JSON.parse(code);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return code;
      }

    case 'html':
      return formatHTML(code);

    case 'css':
      return formatCSS(code);

    case 'xml':
      return formatHTML(code);

    default:
      return code;
  }
}

/**
 * Basic JavaScript formatting as fallback
 */
function basicJavaScriptFormat(code: string): string {
  let formatted = code;
  
  // Basic statement separation
  formatted = formatted.replace(/;(\s*)([a-zA-Z])/g, ';\n$2');
  formatted = formatted.replace(/\{\s*([a-zA-Z])/g, '{\n$1');
  formatted = formatted.replace(/}}/g, '}\n}');
  formatted = formatted.replace(/;}/g, ';\n}');
  
  // Basic indentation
  const lines = formatted.split('\n');
  let indentLevel = 0;
  const indentedLines = [];

  for (let line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      indentedLines.push('');
      continue;
    }

    if (trimmed.startsWith('}')) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    indentedLines.push('  '.repeat(indentLevel) + trimmed);

    if (trimmed.endsWith('{')) {
      indentLevel++;
    }
  }

  return indentedLines.join('\n').trim();
}

/**
 * Format HTML code
 */
function formatHTML(code: string): string {
  let formatted = code;

  // Add newlines before opening tags
  formatted = formatted.replace(/(<[^/][^>]*>)/g, '\n$1');

  // Add newlines after closing tags
  formatted = formatted.replace(/(<\/[^>]*>)/g, '$1\n');

  // Remove spaces between tags
  formatted = formatted.replace(/>\s+</g, '><');

  // Add proper indentation
  const lines = formatted.split('\n');
  let indentLevel = 0;
  const indentedLines = lines.map(line => {
    const trimmed = line.trim();
    if (!trimmed) return '';

    // Decrease indent for closing tags
    if (trimmed.startsWith('</')) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indented = '  '.repeat(indentLevel) + trimmed;

    // Increase indent for opening tags (but not self-closing)
    if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
      indentLevel++;
    }

    return indented;
  });

  return indentedLines.filter(line => line.trim()).join('\n');
}

/**
 * Format CSS code
 */
function formatCSS(code: string): string {
  let formatted = code;

  // Format selectors
  formatted = formatted.replace(/([^{}]+){/g, '$1 {\n  ');

  // Format properties
  formatted = formatted.replace(/([^:;]+):([^:;]+);/g, '  $1: $2;\n');

  // Format closing braces
  formatted = formatted.replace(/\s*}\s*/g, '\n}\n');

  // Clean up multiple newlines
  formatted = formatted.replace(/\n\s*\n\s*\n/g, '\n\n');

  return formatted.trim();
}

/**
 * Format code using Prettier-like rules for languages not supported by Monaco
 * @param code - The code to format
 * @param language - The language of the code
 * @returns string - The formatted code
 */
export function basicFormat(code: string, language: string): string {
  return enhancedFormat(code, language);
}
